/**
 * The action economy — the room-folded decrements (`spend_action`/`spend_reaction`) and the
 * `record_move_reaction_decline` ledger append that
 * enforce nothing, the grants, the self-enforcing `boost` and `spend_action_use`, and the
 * whole-activation `restore_budget` snapshot every one of them inverts through.
 *
 * A leaf-handler group of `./leafReducer`'s switch; see `../reducer` for the tier's contract.
 */

import { CommandError, err, ok } from "@lancer-vtt/shared";
import { applyResolved } from "../applyResolved";
import { invertSteps, type ResolvedStep } from "../steps";
import { DOWN_AND_OUT_STATUS, GRAPPLED_STATUS, IMMOBILIZED_STATUS, REACTION_SLOT_ALLOWANCE, activeAttack, canUseAction, canUseRoundAction, canUseSceneAction, freeActionUseKey, freeActionUses, grantedActionAllowanceBonus, hasStatus, heldAction, isSlowed, isStunned, onBracedTurn, recordSpend, type ActivationState, type Combatant, type CombatantId, type EncounterState, type TurnBudget } from "../types";
import { changed, requireCombatant, type EncounterHandler } from "./context";

export const applyGrantMove: EncounterHandler<"grant_move"> = (state, cmd) => {
  // See `GrantMoveCommand`'s doc for the full rationale — Boost's arithmetic without Boost's
  // cost/ledger/status gates, the acting-unit requirement kept as a STRUCTURAL invariant, and
  // why authored content uses the `grant_move` atom instead.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const a = state.activation;
  if (a.turn.phase !== "acting") {
    return err(new CommandError(`grant_move: nobody is taking a turn — a move budget only exists for the unit mid-turn`));
  }
  if (a.turn.actor !== c.id) {
    return err(new CommandError(`grant_move: combatant ${c.id} is not the acting unit (${a.turn.actor} is) — the grant would land on ${a.turn.actor}`));
  }
  if (!Number.isFinite(cmd.cells) || cmd.cells <= 0) {
    return err(new CommandError(`grant_move: cells must be a positive number (got ${cmd.cells})`));
  }
  // Extra movement is a fresh movement grant, so it clears the flight heading as Boost does
  // (`decisions/0079`). Unlike Boost it keeps the rest of the turn, `attack` included.
  const { heading: _cleared, ...rest } = a.turn;
  const turn = { ...rest, budget: { ...a.turn.budget, move: a.turn.budget.move + cmd.cells } };
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(c.id),
  });
};

export const applySpendAction: EncounterHandler<"spend_action"> = (state, cmd) => {
  // Decrement the action economy — the logged, undoable state change the room folds
  // alongside a resolution it accepted. The reducer ENFORCES NOTHING (the wall is the
  // room's pre-resolve gate); it only subtracts and floors at 0 so replay is total. Two
  // independent ledgers: `points` come off the acting unit's
  // per-turn budget (`activation.turn.budget.actions` — a no-op when nobody is acting,
  // which only happens for a pure out-of-turn reaction), and `reactionBy` decrements the
  // GLOBAL `activation.reactionSlot` AND records a use onto the named-cap
  // `activation.reactionUses` under `reactionName` (`"overwatch"` when omitted; Covering
  // Fire's reaction shot names `"covering_fire"`). `actionId` — when present — additionally
  // records one use onto `activation.turn.budget.actionUses`, the once-per-turn ledger the
  // room's `checkActionUseLegality` gated before this command was folded. The whole prior
  // activation is captured into the `restore_budget` inverse (a floor-at-0 decrement is
  // lossy, and the ledger increments are likewise only losslessly undone by the snapshot).
  const a = state.activation;
  const turn =
    a.turn.phase === "acting"
      ? {
          ...a.turn,
          budget: {
            ...a.turn.budget,
            actions: Math.max(0, a.turn.budget.actions - cmd.points),
            // The cost-keyed spend counter: a
            // point-costing fold closes the "must be done first" window; a zero-point one
            // (every grant-only `request_activate` row, and every Protocol) does not, which is
            // the whole reason the window can't read `actionUses`. Counts `cmd.points` as
            // CHARGED, not the floored subtraction above — an over-spend against an empty
            // budget still counts as having done something.
            ...(cmd.points > 0 ? { spent: recordSpend(a.turn.budget, { actions: cmd.points }) } : {}),
            ...(cmd.actionId !== undefined
              ? { actionUses: { ...a.turn.budget.actionUses, [cmd.actionId]: (a.turn.budget.actionUses?.[cmd.actionId] ?? 0) + 1 } }
              : {}),
            // A Protocol granted action's own
            // `1/turn` cap ledgers onto the PROTOCOL-SAFE sibling, never `actionUses` —
            // mirrors the `actionId` branch just above, one field over.
            ...(cmd.protocolActionId !== undefined
              ? {
                  protocolUses: {
                    ...a.turn.budget.protocolUses,
                    [cmd.protocolActionId]: (a.turn.budget.protocolUses?.[cmd.protocolActionId] ?? 0) + 1,
                  },
                }
              : {}),
          },
        }
      : a.turn;
  let reactionSlot = a.reactionSlot;
  let reactionUses = a.reactionUses;
  if (cmd.reactionBy !== undefined) {
    const have = reactionSlot[cmd.reactionBy] ?? REACTION_SLOT_ALLOWANCE;
    reactionSlot = { ...reactionSlot, [cmd.reactionBy]: Math.max(0, have - 1) };
    reactionUses = bumpReactionUse(reactionUses, cmd.reactionBy, cmd.reactionName ?? "overwatch");
  }
  return ok({
    state: { ...state, activation: { ...a, reactionSlot, reactionUses, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(null),
  });
};

export const applySpendReaction: EncounterHandler<"spend_reaction"> = (state, cmd) => {
  // The dedicated reaction-spender —
  // decrement the GLOBAL reaction slot AND record a named-cap use for a combatant that is
  // NOT the acting actor (Brace's defender spends a reaction out of turn, as a side effect
  // of the attacker's attack). `cmd.name` names the named-cap key, defaulting to
  // `"brace"`.
  // Like `spend_action` the reducer ENFORCES NOTHING — it only subtracts + floors at 0 so
  // replay is total; the eligibility wall is the resolver's reaction registry (which reads
  // both ledgers before offering a reaction). The whole prior activation is captured into
  // the `restore_budget` inverse (a floor-at-0 decrement is lossy), so undoing the
  // attacker's batch refunds the reaction.
  const a = state.activation;
  const have = a.reactionSlot[cmd.combatantId] ?? REACTION_SLOT_ALLOWANCE;
  const reactionSlot = { ...a.reactionSlot, [cmd.combatantId]: Math.max(0, have - 1) };
  const reactionUses = bumpReactionUse(a.reactionUses, cmd.combatantId, cmd.name ?? "brace");
  return ok({
    state: { ...state, activation: { ...a, reactionSlot, reactionUses } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(null),
  });
};

export const applyRecordMoveReactionDecline: EncounterHandler<"record_move_reaction_decline"> = (state, cmd) => {
  // See `RecordMoveReactionDeclineCommand`'s doc: a system-issued ledger append that enforces
  // nothing and spends nothing. A repeat of a live entry is a no-op (still logged, still
  // inverted), so a re-submitted answer can never stack duplicates.
  const a = state.activation;
  const existing = a.moveReactionDeclines ?? [];
  const live = existing.some((d) => d.watcherId === cmd.watcherId && d.moverId === cmd.moverId && d.consumerId === cmd.consumerId);
  const moveReactionDeclines = live ? existing : [...existing, { watcherId: cmd.watcherId, moverId: cmd.moverId, consumerId: cmd.consumerId }];
  return ok({
    state: { ...state, activation: { ...a, moveReactionDeclines } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(null),
  });
};

export const applySetSplitMove: EncounterHandler<"set_split_move"> = (state, cmd) => {
  // See `SetSplitMoveCommand`'s doc: a system-issued hold record that enforces nothing.
  const { splitMove: _prior, ...rest } = state.activation;
  const activation = cmd.split !== undefined ? { ...rest, splitMove: cmd.split } : rest;
  return ok({
    state: { ...state, activation },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: state.activation },
    events: changed(null),
  });
};

export const applyRestoreBudget: EncounterHandler<"restore_budget"> = (state, cmd) => {
  // Internal inverse of `spend_action`/`boost`: restore the whole activation to a captured
  // value (the decrements floored at 0, so a delta-back can't round-trip). Its own inverse
  // is another `restore_budget` to the pre-restore activation.
  const prev = state.activation;
  return ok({
    state: { ...state, activation: cmd.activation },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: prev },
    events: changed(null),
  });
};

/** Why the reducer refuses `boost` for a combatant, in its reject order. `message` is the reducer's own rejection text. */
export const BOOST_BLOCKER_KINDS = ["no_turn", "not_actor", "mid_attack", "no_action", "held", "stunned", "braced", "slowed", "immobilized", "used"] as const;
export type BoostBlocker = { kind: (typeof BOOST_BLOCKER_KINDS)[number]; message: string };

/**
 * The `boost` reducer's legality wall, factored out so a caller can ask why a Boost is refused
 * without parsing the message (the drag callout's "Can't Boost" line, `move-auto-boost`).
 * {@link applyBoost} rejects exactly when this returns non-null, with its `message`.
 */
export function boostBlocker(state: EncounterState, c: Combatant): BoostBlocker | null {
  const a = state.activation;
  if (a.turn.phase !== "acting") {
    return { kind: "no_turn", message: `boost: no active actor — only the unit mid-turn can Boost` };
  }
  if (a.turn.actor !== c.id) {
    return { kind: "not_actor", message: `boost: combatant ${c.id} is not the active actor (${a.turn.actor})` };
  }
  // Mid-attack lock: Boost is a Quick action, so it hits the SAME
  // "finish or end the attack first" wall `prepare` enforces for the lock-holder's own further
  // actions (RAW Core p.62/p.70; Ordnance p.105 — "before you move or take any other action").
  // The standard MOVE is the sole mid-lock exception; Boost is an action,
  // not a move, so it stays blocked. This wall lives HERE (not the room's mid-attack INTENT gate
  // that catches Overcharge et al.) because Boost self-enforces in this reducer and rides in as
  // an `encounter_command`, which bypasses that intent gate entirely —
  // without this check a mid-Skirmish-lock Boost slips through (the Full-action Barrage merely
  // budget-blocks it by accident). Placed before the budget check so the mid-attack reason wins.
  if (activeAttack(a)?.weapons.length) {
    return { kind: "mid_attack", message: `boost: combatant ${c.id} cannot Boost mid-attack — finish or end the attack first (end_attack)` };
  }
  // The cost earmark (`grant_free_action_use{verb:"boost"}`) — e.g. Everest's Hyperspec
  // Fuel Injector's "1/turn, you can Boost as a free action". Read HERE, in the pure
  // domain reducer, because Boost bypasses `intentCost` entirely: it rides in as an
  // `encounter_command` and self-enforces in this case. A `rules`-only implementation of
  // this primitive passes its whole suite with the flagship carrier still charging a
  // Quick action.
  //
  // A held earmark zeroes the COST, which means it must also excuse the budget floor below —
  // a free Boost with zero points left is exactly the case the trait exists to allow. It does
  // NOT touch the once-per-turn wall further down: that is the orthogonal ALLOWANCE axis
  // (`grant_action_allowance`), and a carrier wanting both a free Boost and a second legal one
  // authors both atoms.
  const earmarked = freeActionUses(c.current.resources, "boost") > 0;
  if (!earmarked && a.turn.budget.actions < 1) {
    return { kind: "no_action", message: `boost: combatant ${c.id} has no Quick action left (actions ${a.turn.budget.actions})` };
  }
  // Held-action lockout: a combatant holding a
  // prepared action cannot take ANY action, including Boost — Boost is the one action
  // that self-enforces inside this reducer, so the lockout
  // lands here too, mirroring `move_token`'s own held check just above in this file.
  if (heldAction(a, c.id)) {
    return { kind: "held", message: `boost: combatant ${c.id} is holding a prepared action — drop it (drop_prepare) or wait for it to trigger/lapse before acting` };
  }
  // Status-keyed action legality: Boost is a Quick action, so Stunned/Down-and-Out's "no
  // actions" (RAW p.78; `isStunned()` = Stunned ∨ Shut Down) forbids it
  // outright, and Slowed/Grappled forbid it more specifically ("only the standard move is
  // legal" / Grapple's no-Boost half). Slowed reads through the `isSlowed()`
  // composite (Slowed ∨ Prone, RAW p.77 "PRONE characters are SLOWED") rather than the raw
  // `SLOWED_STATUS` name, so a Prone-but-not-explicitly-Slowed carrier is correctly walled
  // off too — see `isSlowed`'s doc comment. Boost is the ONE
  // action that self-enforces inside the pure domain reducer, so this
  // status wall lives HERE, not in the room/economy.ts `check*` family the other consumers use.
  if (isStunned(c) || hasStatus(c.statuses, DOWN_AND_OUT_STATUS)) {
    return { kind: "stunned", message: `boost: combatant ${c.id} is Stunned (or Shut Down/Down and Out) — no actions until it clears (RAW p.78)` };
  }
  // Brace's price (RAW p.73: "you cannot ... move normally"): Boost's movement is a normal move.
  if (onBracedTurn(c, a)) {
    return { kind: "braced", message: `boost: ${c.name} Braced last turn and can't move normally this turn, so no Boost (RAW p.73)` };
  }
  if (isSlowed(c) || hasStatus(c.statuses, GRAPPLED_STATUS)) {
    return { kind: "slowed", message: `boost: combatant ${c.id} is Slowed/Prone/Grappled — only the standard move is legal, no Boost (RAW p.77-78 / decisions/0054)` };
  }
  // Immobilized joins the Boost reject: Boost's granted movement is voluntary movement,
  // and `IMMOBILIZED` (RAW p.78) forbids voluntary movement outright — without this, an
  // Immobilized carrier could burn its Quick action for move-budget the
  // `voluntaryMoveBar` wall (`movement.ts`) then makes unspendable at `move_token`.
  if (hasStatus(c.statuses, IMMOBILIZED_STATUS)) {
    return { kind: "immobilized", message: `boost: combatant ${c.id} is Immobilized — cannot make any voluntary movements, no Boost (RAW p.78)` };
  }
  // The allowance is the SPAWN-FROZEN base plus any RUNTIME grant
  // (`grant_action_allowance{actionId:"boost"}`). The runtime term must be folded in HERE:
  // `grantedActionAllowanceBonus` is folded into the allowance by
  // `checkActionUseLegality`/`checkActionAvailable` in `@lancer-vtt/rules`, but Boost is the
  // one verb that never reaches them — it self-enforces in this reducer — so without this a
  // triggered grant of a bonus Boost is written to `current.resources` and read by nobody.
  // That matters for RAW, not just tidiness: a free action "can be used to
  // take actions more than once per turn" (Core p.60; p.62 names a granted second BOOST as
  // the worked example), so Everest's Hyperspec Fuel Injector needs its bonus USE to land
  // here as much as it needs its zero COST above.
  const allowance = (c.base.actionAllowances?.boost ?? 1) + grantedActionAllowanceBonus(c.current.resources, "boost");
  if (!canUseAction(a.turn.budget, "boost", allowance)) {
    return { kind: "used", message: `boost: combatant ${c.id} has already used Boost this turn (once per turn)` };
  }
  return null;
}

export const applyBoost: EncounterHandler<"boost"> = (state, cmd) => {
  // Boost (RAW Core p. 73): a Quick action that tops the
  // active unit's move budget up by its Speed. Unlike `spend_action` (a pure room-folded
  // decrement the reducer never gates), Boost is a user-facing gameplay command, so it
  // ENFORCES here like `move_token` — a clean reject (a logged no-op), never a throw, when
  // it isn't legal. The wall: a unit must be mid-turn (`turn.phase === "acting"`), the
  // boosting unit must BE that actor (the move budget only exists for the unit mid-turn —
  // structural), a Quick action must remain (≥1 point), AND Boost must not already be
  // used this turn (`canUseAction`, allowance from `base.actionAllowances.boost` else the
  // RAW default of 1). **Boost is the one action that self-enforces inside the pure domain
  // reducer**, since the shared predicate lives here in `domain`, not
  // `rules`. Apply charges the SAME `budget.actions` field the action economy rides (−1, no
  // parallel counter), adds `base.speed` to `move`, AND records this use onto the ledger.
  // The inverse is `restore_budget` carrying the whole prior activation, so undo restores
  // the spent point, the granted move, AND the ledger entry together.
  //
  // **Flight heading lock: Boost CLEARS the
  // heading, for free.** The success arm below builds a brand-new `turn` object literal
  // naming only `phase`/`actor`/`budget` — it never spreads `a.turn`, so any `heading`
  // (or `attack`) the prior turn carried is dropped by construction, exactly the "a
  // fresh movement grant unlocks a new direction" RAW behavior. No explicit reset code
  // needed, and no new inverse plumbing either: `restore_budget`'s `activation: a`
  // snapshot already carries whatever heading stood before this Boost, so undoing it
  // restores that heading (and redoing re-clears it) automatically.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const a = state.activation;
  const blocked = boostBlocker(state, c);
  // `boostBlocker` already refuses a turn that is not acting; the phase check narrows `a.turn`.
  if (blocked !== null || a.turn.phase !== "acting") return err(new CommandError(blocked?.message ?? "boost: no active actor"));
  const earmarked = freeActionUses(c.current.resources, "boost") > 0;
  const budget: TurnBudget = {
    ...a.turn.budget,
    actions: earmarked ? a.turn.budget.actions : a.turn.budget.actions - 1,
    move: a.turn.budget.move + c.base.speed,
    actionUses: { ...a.turn.budget.actionUses, boost: (a.turn.budget.actionUses?.boost ?? 0) + 1 },
    // The spend counter follows the COST, which is exactly what the
    // earmark zeroes: a paid Boost charges one action point and closes the "must be done
    // first" window; an earmarked (free) Boost charges nothing and leaves it open. That
    // second half is the knowing divergence from the strictest reading of Ordnance's
    // "excepting PROTOCOLS" (Core p.105) that the developer ruled for — see D1. Neither
    // form touches `spent.move`: Boost GRANTS move, it never spends it.
    spent: recordSpend(a.turn.budget, { actions: earmarked ? 0 : 1 }),
  };
  const boosted: EncounterState = { ...state, activation: { ...a, turn: { phase: "acting", actor: c.id, budget } } };
  if (!earmarked) {
    return ok({
      state: boosted,
      inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
      events: changed(c.id),
    });
  }
  // ── The earmark consume, and its COMPOUND inverse ──
  // `restore_budget` restores `state.activation` ONLY — it never touches
  // `combatants[].current.resources`, where the earmark lives. So Boost's ordinary inverse is
  // insufficient here: on its own it would hand back the action point (which this Boost never
  // spent) and silently leave the earmark burnt, so undo-then-redo would charge a point the
  // player was owed for free. The batch pairs the activation restore with the resource
  // restore, mirroring `end_turn`'s Flying-grounding compound inverse in this same file.
  const key = freeActionUseKey("boost");
  const consumed = applyResolved(boosted, [{ op: "resource_delta", target: c.id, key, delta: -1 }]);
  return ok({
    state: consumed,
    inverse: {
      type: "batch",
      commands: [
        { type: "restore_budget", actorId: cmd.actorId, activation: a },
        { type: "apply_resolved", actorId: cmd.actorId, steps: [{ op: "resource_delta", target: c.id, key, delta: 1 }], kind: "free_action_use_restore" },
      ],
    },
    events: changed(c.id),
  });
};

export const applyGrantOvercharge: EncounterHandler<"grant_overcharge"> = (state, cmd) => {
  // Overcharge: the action-economy half,
  // folded by the room alongside the heat resolution (`batch[apply_resolved,
  // grant_overcharge]`). Like `spend_action`, the reducer ENFORCES NOTHING — the once-per-turn
  // / mech-only / on-turn walls are the room's pre-resolve `checkOverchargeLegality`. It
  // grants +1 Quick action to the acting unit (RAW: a free Quick action this turn — Core
  // p.78) AND records the use onto `actionUses.overcharge` (no single-purpose
  // `overcharged` boolean — Overcharge is just one more id in this ledger). A no-op when
  // nobody is acting (defensive). The whole prior activation is captured into the
  // `restore_budget` inverse, so undo restores BOTH the granted action and the ledger entry.
  const a = state.activation;
  const turn =
    a.turn.phase === "acting"
      ? {
          ...a.turn,
          budget: {
            ...a.turn.budget,
            actions: a.turn.budget.actions + 1,
            actionUses: { ...a.turn.budget.actionUses, overcharge: (a.turn.budget.actionUses?.overcharge ?? 0) + 1 },
          },
        }
      : a.turn;
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};

export const applyGrantActionAllowance: EncounterHandler<"grant_action_allowance"> = (state, cmd) => {
  // Initiative (the Everest frame trait — and any other bonus-action-granting trait/CORE
  // power the room folds this for, see
  // `GrantActionAllowanceCommand`'s doc): grants +1 Quick action to the acting unit, exactly
  // like `grant_overcharge` above, MINUS its own ledger stamp — the once-per-scene cadence
  // already rides the generic frozen-cap/`spend_action_use` mechanism the room folds
  // alongside the SAME `request_activate` this command is a sibling of, so a second stamp
  // here would double-book it. A no-op when nobody is acting (defensive). The whole prior
  // activation is captured into the `restore_budget` inverse, so undo restores the granted
  // action.
  const a = state.activation;
  const turn =
    a.turn.phase === "acting"
      ? { ...a.turn, budget: { ...a.turn.budget, actions: a.turn.budget.actions + 1 } }
      : a.turn;
  return ok({
    state: { ...state, activation: { ...a, turn } },
    inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
    events: changed(cmd.combatantId),
  });
};

export const applySpendActionUse: EncounterHandler<"spend_action_use"> = (state, cmd) => {
  // The SELF-ENFORCING stamp for the round/scene cadence grains — mirrors
  // `boost` (the other verb that gates inside the pure domain reducer, because its
  // shared predicate lives here, not `@lancer-vtt/rules`). Unlike
  // `spend_action`/`apply_resolved` (which the room pre-gates before folding), this
  // command carries its own legality: a spent round/scene use is a logged reject, never
  // a silent overwrite. `max` is the per-cadence cap (default 1) the caller supplies —
  // typically sourced from the frozen granted-action descriptor / weapon `Limited {N}`.
  // One door: {@link canUseRoundAction}/{@link canUseSceneAction}
  // are the SAME reads `checkActionAvailable` composes for every other consumer.
  const cR = requireCombatant(state, cmd.combatantId);
  if (!cR.ok) return cR;
  const c = cR.value;
  const max = cmd.max ?? 1;
  // Charge pooling: the CALLER threads the frozen cap's `pool`
  // through when one item minted more than one composite id off the same item-level
  // `Limited N` cap (Smoke/HEX Charges: a thrown grenade AND a deployable mine). The
  // ledger stamps under `pool` when present so every id sharing that pool drains ONE
  // counter; absent `pool` (the common case) keys on `actionId` unchanged.
  const ledgerKey = cmd.pool ?? cmd.actionId;
  if (cmd.grain === "round") {
    const a = state.activation;
    if (!canUseRoundAction(a.actionRoundUses[c.id], ledgerKey, max)) {
      return err(new CommandError(
        `spend_action_use: ${c.id} has already used ${cmd.actionId} this round (max ${max})`,
      ));
    }
    const actionRoundUses = bumpActionRoundUse(a.actionRoundUses, c.id, ledgerKey);
    return ok({
      state: { ...state, activation: { ...a, actionRoundUses } },
      // The whole-activation snapshot inverse every `ActivationState`-mutating command
      // uses (`restore_budget`) — a ledger increment can't invert by delta alone once
      // other activity has touched the same combatant's entry.
      inverse: { type: "restore_budget", actorId: cmd.actorId, activation: a },
      events: changed(c.id),
    });
  }
  // grain === "scene": rides `current.affordanceState`, the per-affordance ledger
  // `decisions/0176` makes the single home for a consumed-use count (`affordance-table` Phase
  // 3.1 deleted the `scene_use:` `current.resources` prefix this used to write beside it).
  //
  // `prev` is PRESENCE-carrying rather than a sentinel — omitted when the slot had no entry at
  // all — so `invertStep` restores genuine absence on undo instead of an explicit `0` residue
  // that merely READS the same under `canUseSceneAction`'s `?? 0`. The sparse-absent convention:
  // a fresh run and a spend-then-undo run must be byte-identical, not merely equivalent.
  if (!canUseSceneAction(c.current.affordanceState, ledgerKey, max)) {
    return err(new CommandError(
      `spend_action_use: ${c.id} has already spent ${cmd.actionId} for the encounter (max ${max})`,
    ));
  }
  // `ledgerKey` is `pool ?? actionId`, so every affordance sharing one item-level `Limited N`
  // charge count drains the SAME slot.
  const prevUses = c.current.affordanceState?.[ledgerKey]?.uses;
  const steps: ResolvedStep[] = [
    { op: "affordance_use_set", target: c.id, key: ledgerKey, uses: (prevUses ?? 0) + 1, ...(prevUses !== undefined ? { prev: prevUses } : {}) },
  ];
  const next = applyResolved(state, steps);
  return ok({
    state: next,
    inverse: { type: "apply_resolved", actorId: cmd.actorId, steps: invertSteps(steps) },
    events: changed(c.id),
  });
};

/**
 * Increment `id`'s use-count for the named reaction `reactionId` — the cap that refreshes at
 * `id`'s own next turn start, a sibling of
 * {@link ../types canUseAction}'s turn-scoped ledger bump. Returns a new `reactionUses` map;
 * pure, no mutation.
 */
function bumpReactionUse(
  uses: ActivationState["reactionUses"],
  id: CombatantId,
  reactionId: string,
): ActivationState["reactionUses"] {
  return { ...uses, [id]: { ...uses[id], [reactionId]: (uses[id]?.[reactionId] ?? 0) + 1 } };
}

/**
 * Increment `id`'s use-count for the ROUND-scoped action `actionId` this round — the
 * round-grain sibling of
 * {@link bumpReactionUse}, identical shape. Returns a new `actionRoundUses` map; pure, no
 * mutation.
 */
function bumpActionRoundUse(
  uses: ActivationState["actionRoundUses"],
  id: CombatantId,
  actionId: string,
): ActivationState["actionRoundUses"] {
  return { ...uses, [id]: { ...uses[id], [actionId]: (uses[id]?.[actionId] ?? 0) + 1 } };
}
